import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Card, CardBody, Row, Col, Table, Badge, Button, Input, Spinner, Alert,
} from "reactstrap";
import { Link } from "react-router-dom";
import {
  getApplicationsByProperty, updateApplicationStatus,
} from "../../../helpers/firestoreService";
import { getApplicantsForApplication } from "../../../helpers/applicantsApi";
import { getIncomeDocumentUrl, getApplicantIncomeDocumentUrl } from "../../../helpers/verificationApi";
import { startBackgroundScreening, downloadScreeningReportPdf } from "../../../helpers/checkrApi";
import useMyProperties from "../../../hooks/useMyProperties";
import PageHero from "../../../components/Common/PageHero";
import ScreeningReportPanel from "./ScreeningReportPanel";

const STATUSES = ["new", "reviewing", "approved", "rejected"];

const statusColor = (s) =>
  s === "approved" ? "success" : s === "rejected" ? "danger" : s === "reviewing" ? "warning" : "info";

const statusLabel = (s) =>
  s === "reviewing" ? "Reviewing" : s === "approved" ? "Approved" : s === "rejected" ? "Rejected" : "New";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const formatDob = (dob) => {
  if (!dob) return "—";
  const d = new Date(`${dob}T00:00:00`);
  return Number.isNaN(d.getTime()) ? dob : d.toLocaleDateString();
};

const SCREENING_LABELS = {
  invited: "Invited",
  in_progress: "In Progress",
  action_needed: "Action Needed",
  complete: "Complete",
  expired: "Expired",
  error: "Error",
};

const screeningColor = (s) =>
  s === "complete" ? "success" : s === "error" || s === "expired" ? "danger" : s === "action_needed" ? "warning" : s ? "info" : "secondary";

/** Every rental application across every property the manager can access. */
const ApplicationsPage = () => {
  document.title = "Applications | Support Portal";

  const { properties, loading: propsLoading } = useMyProperties();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [viewingDocKey, setViewingDocKey] = useState(null);
  const [householdByAppId, setHouseholdByAppId] = useState({});
  const [householdLoading, setHouseholdLoading] = useState({});
  const [householdError, setHouseholdError] = useState({});
  const [coScreeningBusy, setCoScreeningBusy] = useState({});
  const [coScreeningError, setCoScreeningError] = useState({});
  const [coDocViewingKey, setCoDocViewingKey] = useState(null);
  const [screeningBusy, setScreeningBusy] = useState({});
  const [screeningError, setScreeningError] = useState({});
  const [pdfDownloading, setPdfDownloading] = useState({});
  const [pdfError, setPdfError] = useState({});

  useEffect(() => {
    if (propsLoading) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const perProperty = await Promise.all(
          properties.map((p) =>
            getApplicationsByProperty(p.id)
              .then((apps) => apps.map((a) => ({ ...a, property: p })))
              .catch(() => [])
          )
        );
        if (cancelled) return;
        setApplications(
          perProperty.flat().sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0))
        );
      } catch (e) {
        console.error("Load applications error:", e);
        if (!cancelled) setError("Couldn't load your applications.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [properties, propsLoading]);

  const handleStatus = async (app, status) => {
    setError("");
    try {
      await updateApplicationStatus(app.id, status);
      setApplications((prev) => prev.map((a) => (a.id === app.id ? { ...a, status } : a)));
    } catch (e) {
      console.error("Update application status error:", e);
      setError("Couldn't update that application.");
    }
  };

  const toggleExpand = (app) => {
    setExpandedId((id) => (id === app.id ? null : app.id));
    if (!householdByAppId[app.id]) {
      setHouseholdLoading((prev) => ({ ...prev, [app.id]: true }));
      setHouseholdError((prev) => ({ ...prev, [app.id]: false }));
      getApplicantsForApplication(app.id)
        .then((list) => setHouseholdByAppId((prev) => ({ ...prev, [app.id]: list })))
        .catch((e) => {
          console.error(`Failed to load household for application ${app.id}:`, e);
          setHouseholdByAppId((prev) => ({ ...prev, [app.id]: [] }));
          setHouseholdError((prev) => ({ ...prev, [app.id]: true }));
        })
        .finally(() => setHouseholdLoading((prev) => ({ ...prev, [app.id]: false })));
    }
  };

  const handleViewIncomeDoc = async (applicationId, key) => {
    setViewingDocKey(key);
    try {
      const url = await getIncomeDocumentUrl(applicationId, key);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Get income document url error:", e);
      setError("Couldn't open that document. Please try again.");
    } finally {
      setViewingDocKey(null);
    }
  };

  const handleViewCoIncomeDoc = async (applicationId, applicantId, key) => {
    setCoDocViewingKey(key);
    try {
      const url = await getApplicantIncomeDocumentUrl(applicationId, applicantId, key);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Get co-applicant income document url error:", e);
      setError("Couldn't open that document. Please try again.");
    } finally {
      setCoDocViewingKey(null);
    }
  };

  // The primary applicant's status otherwise only updates via Checkr's
  // webhook — this forces a live poll for when that webhook was missed
  // (e.g. Checkr shows "completed" but ours is still stuck "in_progress").
  const handleRefreshScreening = async (applicationId) => {
    setScreeningError((prev) => ({ ...prev, [applicationId]: "" }));
    setScreeningBusy((prev) => ({ ...prev, [applicationId]: true }));
    try {
      const result = await startBackgroundScreening(applicationId);
      setApplications((prev) => prev.map((a) => (a.id === applicationId ? {
        ...a,
        backgroundCheck: { ...(a.backgroundCheck || {}), status: result.status, invitationUrl: result.invitationUrl },
      } : a)));
    } catch (e) {
      console.error("Refresh screening status error:", e);
      setScreeningError((prev) => ({ ...prev, [applicationId]: "Couldn't refresh this screening." }));
    } finally {
      setScreeningBusy((prev) => ({ ...prev, [applicationId]: false }));
    }
  };

  const handleDownloadReportPdf = async (applicationId) => {
    setPdfError((prev) => ({ ...prev, [applicationId]: "" }));
    setPdfDownloading((prev) => ({ ...prev, [applicationId]: true }));
    try {
      await downloadScreeningReportPdf(applicationId);
    } catch (e) {
      console.error("Download screening report PDF error:", e);
      setPdfError((prev) => ({ ...prev, [applicationId]: "Couldn't download this report." }));
    } finally {
      setPdfDownloading((prev) => ({ ...prev, [applicationId]: false }));
    }
  };

  // Screenings for additional adult occupants are normally created
  // server-side the instant the primary applicant pays — this is a manual
  // retry for when that failed (e.g. an unparseable property address).
  const handleRetryCoScreening = async (applicationId, coApplicant) => {
    setCoScreeningError((prev) => ({ ...prev, [coApplicant.id]: "" }));
    setCoScreeningBusy((prev) => ({ ...prev, [coApplicant.id]: true }));
    try {
      const result = await startBackgroundScreening(applicationId, {}, coApplicant.id);
      setHouseholdByAppId((prev) => ({
        ...prev,
        [applicationId]: (prev[applicationId] || []).map((co) => (co.id === coApplicant.id ? {
          ...co,
          backgroundCheck: { ...(co.backgroundCheck || {}), status: result.status, invitationUrl: result.invitationUrl },
        } : co)),
      }));
    } catch (e) {
      console.error("Retry co-applicant screening error:", e);
      setCoScreeningError((prev) => ({ ...prev, [coApplicant.id]: "Couldn't start/refresh this screening." }));
    } finally {
      setCoScreeningBusy((prev) => ({ ...prev, [coApplicant.id]: false }));
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return applications.filter((a) => {
      if (statusFilter !== "all" && (a.status || "new") !== statusFilter) return false;
      if (propertyFilter !== "all" && a.propertyId !== propertyFilter) return false;
      if (!term) return true;
      return [a.name, a.email, a.phone, a.property?.address]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [applications, search, statusFilter, propertyFilter]);

  const counts = useMemo(() => {
    const c = { all: applications.length };
    STATUSES.forEach((s) => { c[s] = applications.filter((a) => (a.status || "new") === s).length; });
    return c;
  }, [applications]);

  const busy = propsLoading || loading;

  return (
    <div className="page-content">
      <Container fluid>
        <PageHero
          title="Applications"
          subtitle="Every applicant across all of your properties, in one place."
        >
          <Badge color="light" className="text-dark fs-6 px-3 py-2">
            <i className="bx bx-file me-1" />
            {counts.all} {counts.all === 1 ? "Application" : "Applications"}
          </Badge>
        </PageHero>

        {error && <Alert color="danger" className="py-2 small">{error}</Alert>}

        <Card>
          <CardBody>
            <Row className="g-2 align-items-center mb-3">
              <Col md={4}>
                <Input
                  bsSize="sm"
                  placeholder="Search applicant, email or property…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
              <Col md={4}>
                <Input
                  type="select"
                  bsSize="sm"
                  value={propertyFilter}
                  onChange={(e) => setPropertyFilter(e.target.value)}
                >
                  <option value="all">All properties</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.address}</option>
                  ))}
                </Input>
              </Col>
              <Col md={4}>
                <div className="btn-group btn-group-sm w-100">
                  <Button
                    color={statusFilter === "all" ? "primary" : "light"}
                    onClick={() => setStatusFilter("all")}
                  >
                    All
                  </Button>
                  {STATUSES.map((s) => (
                    <Button
                      key={s}
                      color={statusFilter === s ? "primary" : "light"}
                      onClick={() => setStatusFilter(s)}
                    >
                      {statusLabel(s)} {counts[s] > 0 && `(${counts[s]})`}
                    </Button>
                  ))}
                </div>
              </Col>
            </Row>

            {busy ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-5">
                <i className="bx bx-file text-muted" style={{ fontSize: "2.5rem" }} />
                <h5 className="text-muted mt-2">No applications</h5>
                <p className="text-muted mb-0">
                  Share a rental application link from a property to start collecting applicants.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 32 }} />
                      <th>Applicant</th>
                      <th>Property</th>
                      <th>Submitted</th>
                      <th>Status</th>
                      <th>Screening</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((a) => (
                      <React.Fragment key={a.id}>
                      <tr>
                        <td>
                          <Button
                            color="link"
                            className="p-0 text-muted"
                            onClick={() => toggleExpand(a)}
                            title={expandedId === a.id ? "Hide screening report" : "View screening report"}
                          >
                            <i className={`bx ${expandedId === a.id ? "bx-chevron-down" : "bx-chevron-right"}`} style={{ fontSize: "1.2rem" }} />
                          </Button>
                        </td>
                        <td>
                          <div className="fw-semibold">{[a.firstName, a.lastName].filter(Boolean).join(" ") || a.name || "—"}</div>
                          <small className="text-muted d-block">{a.email}</small>
                          {a.phone && <small className="text-muted">{a.phone}</small>}
                          {((a.additionalApplicants || []).length > 0 || a.minorsCount > 0) && (
                            <Badge color="light" className="text-dark fw-normal mt-1" pill title="Additional household occupants">
                              <i className="bx bx-group me-1" />
                              {(a.additionalApplicants || []).length + (a.minorsCount > 0 ? 1 : 0) > 0 &&
                                [
                                  (a.additionalApplicants || []).length > 0 && `+${(a.additionalApplicants || []).length} adult${(a.additionalApplicants || []).length > 1 ? "s" : ""}`,
                                  a.minorsCount > 0 && `${a.minorsCount} minor${a.minorsCount > 1 ? "s" : ""}`,
                                ].filter(Boolean).join(", ")}
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Link to={`/properties/${a.propertyId}`} className="text-decoration-none">
                            <i className="bx bx-building-house me-1" />
                            {a.property?.address || a.propertyId}
                          </Link>
                        </td>
                        <td className="text-muted small">{formatDate(a.submittedAt)}</td>
                        <td>
                          <Badge color={statusColor(a.status)} pill>
                            {statusLabel(a.status)}
                          </Badge>
                        </td>
                        <td>
                          {a.backgroundCheck?.status ? (
                            <div className="d-flex align-items-center gap-1">
                              <Badge color={screeningColor(a.backgroundCheck.status)} pill>
                                {SCREENING_LABELS[a.backgroundCheck.status] || a.backgroundCheck.status}
                              </Badge>
                              {a.backgroundCheck.status !== "complete" && (
                                <Button
                                  color="link"
                                  className="p-0 text-muted"
                                  disabled={screeningBusy[a.id]}
                                  onClick={() => handleRefreshScreening(a.id)}
                                  title="Re-check status with Checkr"
                                >
                                  {screeningBusy[a.id] ? <Spinner size="sm" /> : <i className="bx bx-refresh" />}
                                </Button>
                              )}
                              {a.backgroundCheck.reportId && (
                                <Button
                                  color="link"
                                  className="p-0 text-muted"
                                  disabled={pdfDownloading[a.id]}
                                  onClick={() => handleDownloadReportPdf(a.id)}
                                  title="Download report PDF"
                                >
                                  {pdfDownloading[a.id] ? <Spinner size="sm" /> : <i className="bx bx-download" />}
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted small">Not started</span>
                          )}
                          {screeningError[a.id] && (
                            <div className="text-danger small mt-1">{screeningError[a.id]}</div>
                          )}
                          {pdfError[a.id] && (
                            <div className="text-danger small mt-1">{pdfError[a.id]}</div>
                          )}
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <Button
                              color="warning"
                              outline
                              disabled={a.status === "reviewing"}
                              onClick={() => handleStatus(a, "reviewing")}
                              title="Mark reviewing"
                            >
                              <i className="bx bx-time" />
                            </Button>
                            <Button
                              color="success"
                              outline
                              disabled={a.status === "approved"}
                              onClick={() => handleStatus(a, "approved")}
                              title="Approve"
                            >
                              <i className="bx bx-check" />
                            </Button>
                            <Button
                              color="danger"
                              outline
                              disabled={a.status === "rejected"}
                              onClick={() => handleStatus(a, "rejected")}
                              title="Reject"
                            >
                              <i className="bx bx-x" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {expandedId === a.id && (
                        <tr>
                          <td colSpan={7} className="bg-light">
                            <div className="p-3">
                              {/* Everything the applicant submitted except SSN, which is never stored */}
                              <div className="rounded-3 p-3 mb-3 bg-white border">
                                <h6 className="fw-semibold mb-2">
                                  <i className="bx bx-id-card me-1 text-primary" />Applicant Details
                                </h6>
                                <Row className="g-3 small">
                                  <Col xs={6} md={3}><span className="text-muted">Phone</span><div className="fw-medium">{a.phone || "—"}</div></Col>
                                  <Col xs={6} md={3}><span className="text-muted">Date of Birth</span><div className="fw-medium">{formatDob(a.dob)}</div></Col>
                                  <Col xs={6} md={3}><span className="text-muted">Employer</span><div className="fw-medium">{a.employer || "—"}</div></Col>
                                  <Col xs={6} md={3}><span className="text-muted">Monthly Income</span><div className="fw-medium">{a.monthlyIncome ? `$${Number(a.monthlyIncome).toLocaleString()}` : "—"}</div></Col>
                                </Row>
                                <div className="mt-3 pt-3 border-top">
                                  <span className="text-muted small d-block mb-2">Proof of Income</span>
                                  {(a.incomeDocuments || []).length === 0 ? (
                                    <p className="small text-muted mb-0">No documents uploaded yet.</p>
                                  ) : (
                                    <div className="d-flex flex-wrap gap-2">
                                      {a.incomeDocuments.map((d) => (
                                        <Button
                                          key={d.key}
                                          color="light"
                                          size="sm"
                                          disabled={viewingDocKey === d.key}
                                          onClick={() => handleViewIncomeDoc(a.id, d.key)}
                                        >
                                          {viewingDocKey === d.key ? <Spinner size="sm" /> : <><i className="bx bx-file-blank me-1" />{d.filename}</>}
                                        </Button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="mt-3 pt-3 border-top">
                                  <span className="text-muted small d-block mb-2">Screening Report</span>
                                  <ScreeningReportPanel
                                    applicationId={a.id}
                                    currentStatus={a.backgroundCheck?.status}
                                    onComplete={() => handleRefreshScreening(a.id)}
                                  />
                                </div>
                              </div>

                              {/* Additional adult occupants (18+), each with their own info/screening,
                                  plus a headcount of occupants under 18 who don't need their own form. */}
                              {(a.minorsCount > 0 || householdLoading[a.id] || (householdByAppId[a.id] || []).length > 0 || householdError[a.id]) && (
                                <div className="rounded-3 p-3 mb-3 bg-white border">
                                  <h6 className="fw-semibold mb-2">
                                    <i className="bx bx-group me-1 text-primary" />Household
                                  </h6>
                                  {householdError[a.id] && (
                                    <Alert color="warning" className="py-2 small mb-2">
                                      Couldn't load this application's co-applicants. Try re-expanding this row.
                                    </Alert>
                                  )}
                                  {a.minorsCount > 0 && (
                                    <p className="small mb-3">
                                      <span className="text-muted">Occupants under 18:</span> <span className="fw-medium">{a.minorsCount}</span>
                                    </p>
                                  )}
                                  {householdLoading[a.id] ? (
                                    <div className="py-2"><Spinner size="sm" /></div>
                                  ) : (householdByAppId[a.id] || []).length === 0 ? (
                                    !householdError[a.id] && <p className="small text-muted mb-0">No additional adult occupants.</p>
                                  ) : (
                                    <div className="d-flex flex-column gap-2">
                                      {householdByAppId[a.id].map((co) => (
                                        <div key={co.id} className="border rounded p-2">
                                          <div className="d-flex justify-content-between align-items-center">
                                            <span className="fw-medium">{co.name || co.email}{co.relationship ? ` — ${co.relationship}` : ""}</span>
                                            {co.status === "declined" ? (
                                              <Badge color="danger" pill>Declined</Badge>
                                            ) : co.backgroundCheck?.status ? (
                                              <Badge color={screeningColor(co.backgroundCheck.status)} pill>
                                                {SCREENING_LABELS[co.backgroundCheck.status] || co.backgroundCheck.status}
                                              </Badge>
                                            ) : (
                                              <Badge color="secondary" pill>Screening Pending</Badge>
                                            )}
                                          </div>
                                          <Row className="g-2 small mt-1">
                                            <Col xs={6} md={3}><span className="text-muted">Phone</span><div className="fw-medium">{co.phone || "—"}</div></Col>
                                            <Col xs={6} md={3}><span className="text-muted">Date of Birth</span><div className="fw-medium">{formatDob(co.dob)}</div></Col>
                                            <Col xs={6} md={3}><span className="text-muted">Email</span><div className="fw-medium text-truncate">{co.email || "—"}</div></Col>
                                            <Col xs={6} md={3}><span className="text-muted">Employer</span><div className="fw-medium">{co.employer || "—"}</div></Col>
                                            <Col xs={6} md={3}><span className="text-muted">Monthly Income</span><div className="fw-medium">{co.monthlyIncome ? `$${Number(co.monthlyIncome).toLocaleString()}` : "—"}</div></Col>
                                          </Row>
                                          {(co.incomeDocuments || []).length > 0 && (
                                            <div className="mt-2 d-flex flex-wrap gap-2">
                                              {co.incomeDocuments.map((d) => (
                                                <Button
                                                  key={d.key}
                                                  color="light"
                                                  size="sm"
                                                  disabled={coDocViewingKey === d.key}
                                                  onClick={() => handleViewCoIncomeDoc(a.id, co.id, d.key)}
                                                >
                                                  {coDocViewingKey === d.key ? <Spinner size="sm" /> : <><i className="bx bx-file-blank me-1" />{d.filename}</>}
                                                </Button>
                                              ))}
                                            </div>
                                          )}
                                          {coScreeningError[co.id] && (
                                            <Alert color="danger" className="py-1 px-2 small mt-2 mb-0">{coScreeningError[co.id]}</Alert>
                                          )}
                                          {co.status !== "declined" && co.backgroundCheck?.status !== "complete" && (
                                            <Button
                                              color="link"
                                              size="sm"
                                              className="p-0 mt-1"
                                              disabled={coScreeningBusy[co.id]}
                                              onClick={() => handleRetryCoScreening(a.id, co)}
                                            >
                                              {coScreeningBusy[co.id] ? <Spinner size="sm" /> : co.backgroundCheck?.status ? "Refresh Status" : "Start / Resend Screening"}
                                            </Button>
                                          )}
                                          {co.status !== "declined" && co.backgroundCheck?.status && (
                                            <div className="mt-2 pt-2 border-top">
                                              <span className="text-muted small d-block mb-2">Screening Report</span>
                                              <ScreeningReportPanel
                                                applicationId={a.id}
                                                applicantId={co.id}
                                                currentStatus={co.backgroundCheck?.status}
                                                onComplete={() => handleRetryCoScreening(a.id, co)}
                                              />
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ApplicationsPage;
